import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, Paper, Alert, IconButton, Chip, LinearProgress,
  useTheme,
} from '@mui/material';
import { Close, CloudUpload, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { useTenders } from '../../hooks/TenderContext';
import toast from 'react-hot-toast';

// Column mapping: Hebrew to English field names
const COLUMN_MAP = {
  'מס"ד': 'id',
  'מספר מכרז': 'tender_number',
  'מזמין': 'client',
  'נושא המכרז': 'subject',
  'נושא': 'subject',
  'תחום': 'field',
  'סטטוס': 'status',
  'אופן זכייה': 'win_method',
  'מנהל': 'manager',
  'גורם מטפל': 'handler',
  'מפגש מציעים': 'bidders_meeting',
  'שאלות הבהרה': 'clarification_date',
  'מועד הגשה': 'submission_date',
  'תוצאה': 'win_status',
  'תעדוף': 'priority',
  'הערות': 'notes',
};

const ImportExcel = ({ open, onClose }) => {
  const theme = useTheme();
  const { createTender } = useTenders();
  const fileInputRef = useRef(null);
  const [parsedData, setParsedData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState('');

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(e.target.result);
        const sheet = wb.worksheets[0];

        const headers = [];
        sheet.getRow(1).eachCell({ includeEmpty: false }, (cell) => {
          headers[cell.col - 1] = String(cell.value || '');
        });

        const rawData = [];
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return;
          const rowObj = {};
          row.eachCell({ includeEmpty: false }, (cell) => {
            const header = headers[cell.col - 1];
            if (header) rowObj[header] = cell.value;
          });
          if (Object.keys(rowObj).length > 0) rawData.push(rowObj);
        });

        const mapped = rawData.map((row, idx) => {
          const mappedRow = {};
          const rowErrors = [];

          Object.entries(row).forEach(([key, value]) => {
            const fieldName = COLUMN_MAP[String(key).trim()] || String(key).trim();
            mappedRow[fieldName] = value;
          });

          // Validate required fields
          if (!mappedRow.client) rowErrors.push('חסר מזמין');
          if (!mappedRow.subject) rowErrors.push('חסר נושא');

          return { data: mappedRow, errors: rowErrors, rowNum: idx + 2 };
        });

        setParsedData(mapped);
        setErrors(mapped.filter(r => r.errors.length > 0));
      } catch (err) {
        toast.error('שגיאה בקריאת הקובץ');
        console.error(err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    setImporting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const row of parsedData) {
      if (row.errors.length > 0) {
        errorCount++;
        continue;
      }

      try {
        await createTender({
          ...row.data,
          category: 'active',
        });
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setImporting(false);
    toast.success(`יובאו ${successCount} מכרזים בהצלחה${errorCount > 0 ? `, ${errorCount} שגיאות` : ''}`);
    handleClose();
  };

  const handleClose = () => {
    setParsedData([]);
    setErrors([]);
    setFileName('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>ייבוא מ-Excel</Typography>
        <IconButton onClick={handleClose}><Close /></IconButton>
      </DialogTitle>

      <DialogContent>
        {parsedData.length === 0 ? (
          <Box sx={{
            p: 6, textAlign: 'center',
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 3, cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { borderColor: theme.palette.primary.main, bgcolor: theme.palette.action.hover },
          }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h6" gutterBottom>גרור קובץ Excel לכאן</Typography>
            <Typography variant="body2" color="text.secondary">או לחץ לבחירת קובץ (.xlsx)</Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleFileSelect}
            />
          </Box>
        ) : (
          <Box>
            <Alert severity={errors.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {fileName}: {parsedData.length} שורות נמצאו
              {errors.length > 0 && `, ${errors.length} עם שגיאות`}
            </Alert>

            {importing && <LinearProgress sx={{ mb: 2 }} />}

            <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>שורה</TableCell>
                    <TableCell>סטטוס</TableCell>
                    <TableCell>מזמין</TableCell>
                    <TableCell>נושא</TableCell>
                    <TableCell>תחום</TableCell>
                    <TableCell>שגיאות</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedData.slice(0, 50).map((row, i) => (
                    <TableRow key={i} sx={{ bgcolor: row.errors.length > 0 ? '#FFEBEE' : 'transparent' }}>
                      <TableCell>{row.rowNum}</TableCell>
                      <TableCell>
                        {row.errors.length > 0 ? (
                          <ErrorIcon color="error" fontSize="small" />
                        ) : (
                          <CheckCircle color="success" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell>{row.data.client || '—'}</TableCell>
                      <TableCell>{row.data.subject?.substring(0, 40) || '—'}</TableCell>
                      <TableCell>{row.data.field || '—'}</TableCell>
                      <TableCell>
                        {row.errors.map((e, j) => (
                          <Chip key={j} label={e} size="small" color="error" sx={{ mr: 0.5 }} />
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">ביטול</Button>
        {parsedData.length > 0 && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing || parsedData.filter(r => r.errors.length === 0).length === 0}
          >
            ייבוא {parsedData.filter(r => r.errors.length === 0).length} מכרזים
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportExcel;
